import React, { useEffect, useRef, useState } from 'react';

// A one-time, account-level announcement. There is deliberately no close button,
// no click-outside and no Escape: the only way past it is the button, and the
// press is recorded on the account so it does not reappear on another device.
// Pressing it records that the announcement was presented and acknowledged.
export const ExploreAnnouncement = ({ announcementId, onAcknowledge }) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  // Only a recorded acknowledgement counts. If it cannot be saved the modal
  // stays put and says so, rather than letting the member think it is done and
  // meeting it again next time.
  const acknowledge = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await onAcknowledge(announcementId);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="announce-overlay" data-testid="explore-announcement">
      <div
        className="announce-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="announce-title"
      >
        <h2 className="announce-title" id="announce-title" data-testid="announcement-title">
          New: Explore recommendations
        </h2>

        <p className="announce-text">
          irecommend now gives you another way to discover and share recommendations.
        </p>
        <p className="announce-text">
          You can explore recommendations that other irecommend members have chosen to share with
          Everyone on irecommend.
        </p>
        <p className="announce-text">
          When you make a recommendation, you can choose who can see it:
        </p>

        <div className="announce-option">
          <p className="announce-option-label">My connections</p>
          <p className="announce-text">
            Only people you&apos;re connected with can see your recommendation.
          </p>
        </div>
        <p className="announce-or">OR</p>
        <div className="announce-option">
          <p className="announce-option-label">Everyone on irecommend</p>
          <p className="announce-text">
            Other signed-in irecommend members can discover your recommendation in Explore.
          </p>
        </div>

        <p className="announce-text">
          For example: If you recommend a great caf&eacute; and choose Everyone on irecommend,
          someone who isn&apos;t connected with you could discover and save your recommendation.
        </p>
        <p className="announce-text">
          Your existing recommendations have not been made more widely available. They remain
          visible only to your connections unless you choose to change their visibility.
        </p>

        {failed && (
          <p className="announce-error" role="alert" data-testid="announcement-error">
            We couldn&apos;t save that just now. Please check your connection and try again.
          </p>
        )}

        <button
          type="button"
          ref={buttonRef}
          className="announce-btn"
          onClick={acknowledge}
          disabled={busy}
          data-testid="announcement-acknowledge"
        >
          {failed ? 'Try again' : 'Got it — continue'}
        </button>
      </div>
    </div>
  );
};
